import type { ScheduledRunOutcome, ScheduledRunPoint } from '@numeo/types'

/** One lane of the timeline: a job and the runs it had in the window. */
interface TimelineLane {
    job: string
    runs: ScheduledRunPoint[]
    failures: number
}

/** Busiest job first, so what fires most is at the top; ties by name to keep lanes from swapping between refreshes. */
export function toLanes(runs: ScheduledRunPoint[], search: string): TimelineLane[] {
    const q = search.trim().toLowerCase()
    const byJob = new Map<string, TimelineLane>()
    for (const run of runs) {
        if (q && !run.job.toLowerCase().includes(q)) continue
        const lane = byJob.get(run.job) ?? { job: run.job, runs: [], failures: 0 }
        lane.runs.push(run)
        if (run.outcome === 'failed' || run.outcome === 'timed_out') lane.failures += 1
        byJob.set(run.job, lane)
    }
    return [...byJob.values()].sort((a, b) => b.runs.length - a.runs.length || a.job.localeCompare(b.job))
}

/** Axis gridlines: every 10 min for an hour, 15 min for two, an hour for six. */
export function gridStepMs(hours: number): number {
    if (hours <= 1) return 10 * 60_000
    if (hours <= 2) return 15 * 60_000
    return 60 * 60_000
}

/** Gridline times inside the window, on whole steps of the viewer's clock. */
export function gridTicks(from: number, to: number, stepMs: number): number[] {
    const offset = new Date(from).getTimezoneOffset() * 60_000
    const ticks: number[] = []
    for (let t = Math.ceil((from - offset) / stepMs) * stepMs + offset; t <= to; t += stepMs) ticks.push(t)
    return ticks
}

/** "12/h" or "0.5/h": how often a job fired across the window. */
export function formatRate(count: number, hours: number): string {
    const perHour = count / hours
    return `${perHour >= 10 ? Math.round(perHour) : Number(perHour.toFixed(1))}/h`
}

/** Fill for a run's mark, matching the outcome colours the job list uses. */
export const OUTCOME_FILL: Record<ScheduledRunOutcome, string> = {
    succeeded: 'fill-emerald-500',
    failed: 'fill-rose-500',
    timed_out: 'fill-amber-500',
    skipped: 'fill-slate-400',
}

export const OUTCOME_SWATCH: Record<ScheduledRunOutcome, string> = {
    succeeded: 'bg-emerald-500',
    failed: 'bg-rose-500',
    timed_out: 'bg-amber-500',
    skipped: 'bg-slate-400',
}
